import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FormErrorMessage } from '@/shared/components/features/form/FormErrorMessage';
import { FormRadio } from '@/shared/components/features/form/FormRadio';
import { FormSubmitButtons } from '@/shared/components/features/form/FormSubmitButtons';
import { FormText } from '@/shared/components/features/form/FormText';
import { FormTextArea } from '@/shared/components/features/form/FormTextArea';
import { BreadCrumbs } from '@/shared/components/parts/BreadCrumbs';
import { MainContainer } from '@/shared/components/parts/MainContainer';
import { Spacer } from '@/shared/components/parts/Spacer';
import { TextLinkWithArrow } from '@/shared/components/parts/TextLinkWithArrow';
import { generateFormikInputFieldOptions } from '@/shared/helpers/form/generateFieldOptions';
import { getFormikErrorMessage } from '@/shared/helpers/form/getFormikErrorMessage';
import { yupValidations } from '@/shared/helpers/form/yupValidations';
import { INQUIRY_TYPE_MAP } from '../../../_constants/inquiry';
import type { UseInquiry } from '../index.hook';

export const InputForm: React.FC<{
  state: UseInquiry['state'];
  sendInquiry: UseInquiry['sendInquiry'];
  isSending: boolean;
}> = ({ state, sendInquiry, isSending }) => {
  const formik = useFormik({
    initialValues: state.formValues,
    validationSchema: Yup.object().shape({
      detail: yupValidations.required,
      contactEmail: yupValidations.email,
      registeredEmail: yupValidations.optEmail,
    }),
    onSubmit: sendInquiry,
  });

  return (
    <MainContainer>
      <Spacer level={3} />
      <FormErrorMessage message={state.errorMessage} />
      <h1 className="tw-text-h2 tw-text-main-100 tw-halt">お問い合わせ</h1>
      <Spacer level={0.5} />
      <BreadCrumbs
        data={[
          {
            path: '/',
            text: 'ヘルプセンター',
          },
          {
            path: '/inquiry',
            text: 'お問い合わせ',
          },
        ]}
      />
      <Spacer level={2} />
      <div className="tw-rounded-4 tw-bg-base-lv3 tw-p-[1rem]">
        <div className="tw-text-h5 tw-font-bold tw-text-main-100">
          お問い合わせの前に、こちらをご確認ください。
        </div>
        <Spacer level={1} />
        <div className="tw-grid tw-grid-cols-1 tw-gap-[0.5rem]">
          {[
            {
              href: '/systemtrouble',
              text: '緊急のお知らせ',
            },
            {
              href: '/guide',
              text: 'よくある質問',
            },
          ].map(({ href, text }) => (
            <div key={href}>
              <TextLinkWithArrow href={href} text={text} />
            </div>
          ))}
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit();
        }}
      >
        <Spacer level={2} />
        <div className="tw-text-h4 tw-font-bold tw-text-main-100">
          お問い合わせ項目
        </div>
        <Spacer level={1} />
        <div className="tw-grid tw-grid-cols-1 tw-gap-[0.5rem]">
          {Array.from(INQUIRY_TYPE_MAP).map(([key, value]) => (
            <div key={key}>
              <FormRadio
                value={key}
                name="inquiryType"
                onChange={formik.handleChange}
                checked={key === formik.values.inquiryType}
                text={value}
              />
            </div>
          ))}
        </div>

        <Spacer level={2} />
        <div className="tw-text-h4 tw-font-bold tw-text-main-100">内容</div>
        <Spacer level={1} />
        <FormTextArea
          placeholder={'具体的にご記入ください'}
          onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
            formik.setFieldValue('detail', e.target.innerText);
          }}
          validationMessage={getFormikErrorMessage(formik, 'detail')}
        />

        <Spacer level={2} />
        <div className="tw-text-h4 tw-font-bold tw-text-main-100">
          お客様情報
        </div>
        <Spacer level={1} />
        <FormText
          {...generateFormikInputFieldOptions(formik, 'contactEmail', 'email')}
          label="ご連絡先メールアドレス"
          validationMessage={getFormikErrorMessage(formik, 'contactEmail')}
        />

        <Spacer level={0.5} />
        <div className="tw-text-base3 tw-text-main-40">
          ご入力いただいたメールアドレスへ回答をお送りいたします。
        </div>

        {!state.isLoggedIn && (
          <>
            <Spacer level={1} />
            <FormText
              {...generateFormikInputFieldOptions(
                formik,
                'registeredEmail',
                'email'
              )}
              label="ご登録のメールアドレス"
              validationMessage={getFormikErrorMessage(
                formik,
                'registeredEmail'
              )}
            />
            <Spacer level={0.5} />
            <div className="tw-text-base2 tw-text-main-40">
              ※アカウント情報の確認のため、ご連絡先のメールアドレスと違う場合はご入力ください。
              <br />
              ※利用開始前のお客様は空欄のまま送信してください。
            </div>
          </>
        )}
        <Spacer level={3} />

        <FormSubmitButtons
          buttons={[
            {
              buttonType: 'button',
              text: 'お問い合わせを送信',
              type: 'submit',
              disabled: isSending,
              isLoading: isSending,
            },
          ]}
        />
      </form>
      <Spacer level={4} />
    </MainContainer>
  );
};
